import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";

/**
 * Activity Log — the story of one developer's day, told as a rich timeline.
 *
 * Reads the same real engine as the Day view (GET /tasks/day-timeline:
 * attendance + task status history), but presents it as a chronological
 * narrative instead of bars:
 *
 *   arrived → started X → switched to Y (X auto-paused after 1h 20m)
 *   → resumed X (was paused 45m) → completed X → … → checked out
 *
 * Extras that make it readable at a glance: durations on every action
 * ("after 1h 20m of work"), red/gray gap chips for paused/idle stretches,
 * one color dot per task, and a red flag when the checkout is missing.
 */

// ---------- time helpers (server times are "YYYY-MM-DD HH:mm:ss") ----------

const fmtYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseDT = (s) => {
  if (!s) return null;
  const [d, t] = s.split(" ");
  const [y, m, dd] = d.split("-").map(Number);
  const [h, mi, se] = (t || "00:00:00").split(":").map(Number);
  return new Date(y, m - 1, dd, h, mi, se || 0);
};

const minutesOfDay = (dt) => dt.getHours() * 60 + dt.getMinutes() + dt.getSeconds() / 60;

const fmtClock = (s) => {
  const dt = typeof s === "string" ? parseDT(s) : s;
  if (!dt) return "—";
  let h = dt.getHours();
  const m = dt.getMinutes();
  const p = h < 12 ? "AM" : "PM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${p}`;
};

const fmtDurMin = (minutes) => {
  const s = Math.round(minutes * 60);
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, "0")}m`;
};

const fmtHours = (seconds) => `${((seconds || 0) / 3600).toFixed(1)}h`;

// Task chips are colored by the task's CURRENT status — the exact same colors
// as the Backlog page, so status reads identically across the whole app.
const STATUS_COLORS = {
  "To Do": "#6c757d", // gray
  "In Progress": "#0d6efd", // blue
  Pause: "#dc3545", // red
  Review: "#fd7e14", // orange
  Done: "#198754", // green
};

// Visual identity of each action on the timeline.
const ACTION = {
  check_in: { icon: "pi-sign-in", color: "#059669", bg: "#ecfdf5" },
  check_out: { icon: "pi-sign-out", color: "#475569", bg: "#f1f5f9" },
  start: { icon: "pi-play", color: "#2563eb", bg: "#eff6ff" },
  resume: { icon: "pi-replay", color: "#0d9488", bg: "#f0fdfa" },
  pause: { icon: "pi-pause", color: "#dc2626", bg: "#fef2f2" },
  switch: { icon: "pi-arrow-right-arrow-left", color: "#7c3aed", bg: "#f5f3ff" },
  done: { icon: "pi-check-circle", color: "#16a34a", bg: "#f0fdf4" },
  review: { icon: "pi-eye", color: "#ca8a04", bg: "#fefce8" },
  to_do: { icon: "pi-plus-circle", color: "#94a3b8", bg: "#f8fafc" },
  flag: { icon: "pi-flag-fill", color: "#dc2626", bg: "#fef2f2" },
  carry: { icon: "pi-forward", color: "#2563eb", bg: "#eff6ff" },
  quick: { icon: "pi-bolt", color: "#64748b", bg: "#f8fafc" },
};

const WORK_TYPES = ["check_in", "check_out", "start", "resume", "pause", "switch", "done", "review"];

const ActivityLog = () => {
  const toast = useRef(null);

  const [date, setDate] = useState(new Date());
  const [developer, setDeveloper] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreations, setShowCreations] = useState(false);

  const dateStr = fmtYMD(date);
  const isToday = dateStr === fmtYMD(new Date());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // One call brings the whole team's day (or just yourself, if not
        // privileged) — switching developer is then instant.
        const res = await Http.get(`/tasks/day-timeline?date=${dateStr}`);
        if (!cancelled) setData(res?.data?.data || null);
      } catch (e) {
        if (!cancelled) {
          setData(null);
          toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load the activity log.", life: 4000 });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateStr]);

  // Developer options come from the payload itself (scoped by the server).
  const developerOptions = useMemo(() => {
    if (!data?.developers) return [];
    return [...data.developers]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((d) => ({ label: d.name, value: d.developer_id }));
  }, [data]);

  useEffect(() => {
    if (!data?.developers?.length) return;
    if (developer && data.developers.some((d) => d.developer_id === developer)) return;
    // Default to the busiest person of the day, so the page never opens empty.
    const busiest = [...data.developers].sort((a, b) => b.events.length - a.events.length)[0];
    setDeveloper(busiest.developer_id);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const dev = data?.developers?.find((d) => d.developer_id === developer) || null;

  // ---------- build the narrative timeline for the selected developer ----------

  const view = useMemo(() => {
    if (!dev) return null;

    // task id -> current status (the segments carry it; events do not).
    const statusByTask = {};
    (dev.segments || []).forEach((s) => {
      statusByTask[s.task_id] = s.task_status;
    });
    const taskColor = (id) => STATUS_COLORS[statusByTask[id]] || "#6c757d";

    // Chronological events with minutes for the maths.
    const evs = dev.events
      .map((e, i) => ({ ...e, m: minutesOfDay(parseDT(e.at)), seq: i }))
      .sort((a, b) => a.m - b.m || a.seq - b.seq);

    // Annotate durations: how long each task had been worked when it stopped,
    // and how long it sat paused when it came back.
    const lastActive = {};
    const lastPause = {};
    evs.forEach((e) => {
      if (e.task_id == null) return;
      if (e.type === "start" || e.type === "resume") {
        if (lastPause[e.task_id] != null) e.wasPausedMin = e.m - lastPause[e.task_id];
        delete lastPause[e.task_id];
        lastActive[e.task_id] = e.m;
      } else if (["pause", "switch", "done", "review"].includes(e.type)) {
        if (lastActive[e.task_id] != null) e.workedMin = e.m - lastActive[e.task_id];
        delete lastActive[e.task_id];
        if (e.type === "pause" || e.type === "switch") lastPause[e.task_id] = e.m;
      }
    });

    // While-paused intervals (for coloring quiet gaps red).
    const pausedIvs = [];
    evs.forEach((e) => {
      if (e.type !== "pause" && e.type !== "switch") return;
      const next = evs.find((x) => x.task_id === e.task_id && x.m > e.m + 0.001 && ["start", "resume", "done", "review"].includes(x.type));
      pausedIvs.push({ sM: e.m, eM: next ? next.m : 24 * 60, title: e.task_title });
    });

    // Merge each auto-pause with the start that caused it → one "switched to"
    // entry that tells both halves of the story. Two passes, because the pause
    // and the start land at the same moment in either order: first PAIR them,
    // then emit — so a switch is never printed on its own when its start
    // appears later in the list.
    const pairFor = new Map(); // start/resume seq -> its switch event
    const usedSwitch = new Set();
    evs.forEach((e) => {
      if (e.type !== "start" && e.type !== "resume") return;
      const sw = evs.find(
        (x) => !usedSwitch.has(x.seq) && x.type === "switch" && x.task_id !== e.task_id && Math.abs(x.m - e.m) <= 5 / 60 + 0.001
      );
      if (sw) {
        usedSwitch.add(sw.seq);
        pairFor.set(e.seq, sw);
      }
    });
    const merged = [];
    evs.forEach((e) => {
      if (e.type === "switch" && usedSwitch.has(e.seq)) return; // told inside the paired entry
      const sw = pairFor.get(e.seq);
      if (sw) {
        merged.push({ ...e, type: "switch", switchedFrom: sw.task_title, switchedFromWorkedMin: sw.workedMin, wasResume: e.type === "resume" });
      } else {
        merged.push(e);
      }
    });

    // Apply the "task creations" filter.
    let visible = merged.filter((e) => WORK_TYPES.includes(e.type) || (showCreations && e.type === "to_do"));

    // Working segments in minutes — to tell "quiet but working" from "idle".
    const segIvs = (dev.segments || []).map((s) => ({
      sM: minutesOfDay(parseDT(s.start)),
      eM: minutesOfDay(parseDT(s.end)),
      startStr: s.start,
      task_id: s.task_id,
      title: s.title,
      started_earlier: s.started_earlier,
    }));

    // Tasks carried over from a previous day get an explicit entry (they have
    // no "start" event today, yet the developer is working on them).
    const attWindows = dev.attendance || [];
    const checkInM = attWindows.length ? minutesOfDay(parseDT(attWindows[0].check_in)) : null;
    segIvs
      .filter((s) => s.started_earlier)
      .forEach((s, i) => {
        const atCheckIn = checkInM != null && checkInM > s.sM && checkInM < s.eM;
        visible.push({
          type: "carry",
          task_id: s.task_id,
          task_title: s.title,
          m: (atCheckIn ? checkInM : s.sM) + 0.01, // right after the check-in row
          at: atCheckIn ? attWindows[0].check_in : s.startStr,
          seq: 10000 + i,
        });
      });
    visible.sort((a, b) => a.m - b.m || a.seq - b.seq);

    // Collapse instant start → review/done flips into ONE "quick" line.
    const collapsed = [];
    for (let i = 0; i < visible.length; ) {
      const e = visible[i];
      // Only true starters collapse into a "quick" line — a lone auto-pause
      // (switch without a paired start) is an ending, not a beginning.
      if ((e.type === "start" || (e.type === "switch" && e.switchedFrom)) && e.task_id != null) {
        let j = i + 1;
        let last = null;
        while (j < visible.length && visible[j].task_id === e.task_id && ["review", "done"].includes(visible[j].type) && visible[j].m - e.m <= 3) {
          last = visible[j];
          j += 1;
        }
        if (last) {
          collapsed.push({ ...e, type: "quick", finalType: last.type, spanMin: last.m - e.m, wasSwitch: e.type === "switch" });
          i = j;
          continue;
        }
      }
      collapsed.push(e);
      i += 1;
    }
    visible = collapsed;

    // Interleave gap chips between events: red = a task sat paused, blue = the
    // developer was inside a long working stretch (no event, but on a task),
    // gray = truly idle.
    const entries = [];
    visible.forEach((e, i) => {
      if (i > 0) {
        const prev = visible[i - 1];
        const gapMin = e.m - prev.m;
        if (gapMin >= 15 && prev.type !== "check_out") {
          // Working beats paused: if a task was actively In Progress through
          // this quiet stretch, the developer was NOT idle — even if another
          // task sat paused at the same time. When several segments qualify
          // (overlapping legacy data), prefer the task from the previous
          // entry — the one they most recently picked up.
          const covers = (s) => Math.min(s.eM, e.m) - Math.max(s.sM, prev.m) >= gapMin * 0.6;
          const working = segIvs.find((s) => s.task_id === prev.task_id && covers(s)) || segIvs.find(covers);
          const paused = working ? null : pausedIvs.find((p) => p.sM < e.m - 0.25 && p.eM > prev.m + 0.25);
          entries.push({
            kind: "gap",
            key: `gap${i}`,
            minutes: gapMin,
            working: working ? working.title : null,
            paused: !!paused,
            pausedTitle: paused ? paused.title : null,
          });
        }
      }
      entries.push({ kind: "event", key: `e${e.seq}`, ...e });
    });

    // A missing checkout ends the story with a red flag at the 7pm cap.
    const att = dev.attendance || [];
    const forgot = att.find((a) => a.forgot_checkout);
    if (forgot) {
      entries.push({
        kind: "event",
        key: "flag",
        type: "flag",
        at: forgot.effective_check_out,
        m: minutesOfDay(parseDT(forgot.effective_check_out)),
      });
    }

    const taskCount = new Set(dev.events.filter((e) => e.task_id != null).map((e) => e.task_id)).size;
    const pauseCount = dev.events.filter((e) => e.type === "pause" || e.type === "switch").length;

    return { entries, taskColor, taskCount, pauseCount, forgot: !!forgot, attendance: att };
  }, [dev, showCreations]);

  // ---------- entry texts ----------

  const TaskChip = ({ e, title }) => (
    <span className="al-task">
      <span className="al-task-dot" style={{ background: view.taskColor(e.task_id) }} />
      {title || e.task_title}
    </span>
  );

  const entryBody = (e) => {
    switch (e.type) {
      case "check_in":
        return <span className="al-strong">Arrived — checked in</span>;
      case "check_out":
        return <span className="al-strong">Left — checked out</span>;
      case "start":
        return (
          <>
            Started working on <TaskChip e={e} />
          </>
        );
      case "resume":
        return (
          <>
            Resumed <TaskChip e={e} />
            {e.wasPausedMin != null && <span className="al-note"> — was paused {fmtDurMin(e.wasPausedMin)}</span>}
          </>
        );
      case "switch":
        // A lone auto-pause (its causing start is not in this developer's
        // log) is an ending, not a beginning — say who got paused.
        if (!e.switchedFrom) {
          return (
            <>
              <TaskChip e={e} />
              <span className="al-note"> auto-paused — switched to another task</span>
              {e.workedMin != null && <span className="al-note"> (after {fmtDurMin(e.workedMin)} of work)</span>}
            </>
          );
        }
        return (
          <>
            {e.wasResume ? "Switched back to " : "Switched to "}
            <TaskChip e={e} />
            <div className="al-sub">
              <i className="pi pi-pause" style={{ fontSize: 10, marginRight: 4, color: "#dc2626" }} />
              “{e.switchedFrom}” auto-paused
              {e.switchedFromWorkedMin != null && <> after {fmtDurMin(e.switchedFromWorkedMin)} of work</>}
            </div>
          </>
        );
      case "pause":
        return (
          <>
            Paused <TaskChip e={e} />
            {e.workedMin != null && <span className="al-note"> — after {fmtDurMin(e.workedMin)} of work</span>}
            {e.comment && <div className="al-sub al-comment">{e.comment}</div>}
          </>
        );
      case "done":
        return (
          <>
            Completed <TaskChip e={e} />
            {e.workedMin != null && <span className="al-note"> — after {fmtDurMin(e.workedMin)} of work</span>}
          </>
        );
      case "review":
        return (
          <>
            Sent <TaskChip e={e} /> to review
            {e.workedMin != null && <span className="al-note"> — after {fmtDurMin(e.workedMin)} of work</span>}
          </>
        );
      case "to_do":
        return (
          <>
            <span className="al-muted">Task created / queued:</span> <TaskChip e={e} />
          </>
        );
      case "carry":
        return (
          <>
            Continuing <TaskChip e={e} />
            <span className="al-note"> — carried over, In Progress since a previous day</span>
          </>
        );
      case "quick":
        return (
          <>
            {e.finalType === "done" ? "Quick-completed " : "Quick-reviewed "}
            <TaskChip e={e} />
            <span className="al-note"> — started and {e.finalType === "done" ? "marked done" : "sent to review"} in {fmtDurMin(e.spanMin)}</span>
            {e.wasSwitch && e.switchedFrom && (
              <div className="al-sub">
                <i className="pi pi-pause" style={{ fontSize: 10, marginRight: 4, color: "#dc2626" }} />
                “{e.switchedFrom}” auto-paused
                {e.switchedFromWorkedMin != null && <> after {fmtDurMin(e.switchedFromWorkedMin)} of work</>}
              </div>
            )}
          </>
        );
      case "flag":
        return <span className="al-strong al-danger">No checkout recorded — hours counted till 7pm</span>;
      default:
        return e.type;
    }
  };

  const changeDay = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d);
  };

  const headerDate = date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  return (
    <div>
      <IsLogin />
      <Toast ref={toast} />

      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Activity Log</h5>
        </div>

        <div className="am-pagebody">
          <div className="al-wrap">
            {/* Controls */}
            <div className="al-controls card">
              <Dropdown
                value={developer}
                options={developerOptions}
                onChange={(e) => setDeveloper(e.value)}
                placeholder="Select developer"
                filter={developerOptions.length > 8}
                style={{ minWidth: 220 }}
              />
              <div className="al-datenav">
                <Button icon="pi pi-chevron-left" className="p-button-text p-button-sm" onClick={() => changeDay(-1)} />
                <Calendar value={date} onChange={(e) => e.value && setDate(e.value)} showIcon dateFormat="dd M yy" />
                <Button icon="pi pi-chevron-right" className="p-button-text p-button-sm" onClick={() => changeDay(1)} disabled={isToday} />
                {!isToday && <Button label="Today" className="p-button-text p-button-sm" onClick={() => setDate(new Date())} />}
              </div>
              <label className="al-toggle">
                <Checkbox checked={showCreations} onChange={(e) => setShowCreations(e.checked)} />
                <span>show task creations</span>
              </label>
            </div>

            {loading ? (
              <div className="text-center p-5">
                <ProgressSpinner />
                <p className="mt-3">Loading the day's activity…</p>
              </div>
            ) : !dev || !view ? (
              <div className="al-empty card">
                <i className="pi pi-calendar" />
                <p>No data for this day.</p>
              </div>
            ) : (
              <>
                {/* Day summary */}
                <div className="al-summary card">
                  <div className="al-summary-head">
                    <div>
                      <div className="al-devname">{dev.name}</div>
                      <div className="al-date">{headerDate}</div>
                    </div>
                    {view.forgot && (
                      <span className="al-flagchip">
                        <i className="pi pi-flag-fill" /> forgot checkout
                      </span>
                    )}
                  </div>
                  <div className="al-stats">
                    <div className="al-stat">
                      <div className="al-stat-label">Arrived</div>
                      <div className="al-stat-value" style={{ color: "#059669" }}>
                        {view.attendance.length ? fmtClock(view.attendance[0].check_in) : "—"}
                      </div>
                    </div>
                    <div className="al-stat">
                      <div className="al-stat-label">Left</div>
                      <div className="al-stat-value">
                        {view.attendance.length
                          ? view.attendance[view.attendance.length - 1].check_out
                            ? fmtClock(view.attendance[view.attendance.length - 1].check_out)
                            : view.attendance[view.attendance.length - 1].still_in
                            ? "still in"
                            : "no checkout"
                          : "—"}
                      </div>
                    </div>
                    <div className="al-stat">
                      <div className="al-stat-label">Worked</div>
                      <div className="al-stat-value" style={{ color: "#059669", fontWeight: 700 }}>
                        {fmtHours(dev.worked_seconds)}
                      </div>
                    </div>
                    <div className="al-stat">
                      <div className="al-stat-label">In office</div>
                      <div className="al-stat-value">{fmtHours(dev.in_office_seconds)}</div>
                    </div>
                    <div className="al-stat">
                      <div className="al-stat-label">Tasks touched</div>
                      <div className="al-stat-value">{view.taskCount}</div>
                    </div>
                    <div className="al-stat">
                      <div className="al-stat-label">Pauses</div>
                      <div className="al-stat-value">{view.pauseCount}</div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                {view.entries.length === 0 ? (
                  <div className="al-empty card">
                    <i className="pi pi-moon" />
                    <p>Nothing happened this day — no attendance and no task activity.</p>
                  </div>
                ) : (
                  <div className="al-timeline card">
                    {view.entries.map((en) => {
                      if (en.kind === "gap") {
                        return (
                          <div key={en.key} className="al-row al-gaprow">
                            <div className="al-time" />
                            <div className="al-spine">
                              <div className={`al-gapline ${en.paused ? "al-gapline-paused" : ""}`} />
                            </div>
                            <div className="al-gapchip-wrap">
                              <span
                                className={`al-gapchip ${en.paused ? "al-gapchip-paused" : ""} ${en.working ? "al-gapchip-working" : ""}`}
                              >
                                {en.working ? (
                                  <>
                                    <i className="pi pi-play" style={{ fontSize: 9 }} /> focused on “{en.working}” · {fmtDurMin(en.minutes)}
                                  </>
                                ) : en.paused ? (
                                  <>
                                    <i className="pi pi-pause" style={{ fontSize: 9 }} /> “{en.pausedTitle}” stayed paused ·{" "}
                                    {fmtDurMin(en.minutes)}
                                  </>
                                ) : (
                                  <>no active task · {fmtDurMin(en.minutes)}</>
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      }

                      // A lone auto-pause wears the pause identity (red ⏸),
                      // not the purple "switched" arrows.
                      const meta = (en.type === "switch" && !en.switchedFrom ? ACTION.pause : ACTION[en.type]) || ACTION.to_do;
                      return (
                        <div key={en.key} className="al-row">
                          <div className="al-time">{fmtClock(en.at)}</div>
                          <div className="al-spine">
                            <span className="al-dot" style={{ backgroundColor: meta.bg, borderColor: meta.color, color: meta.color }}>
                              <i className={`pi ${meta.icon}`} />
                            </span>
                          </div>
                          <div className="al-card" style={{ borderLeftColor: meta.color }}>
                            {entryBody(en)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Status color legend (same colors as the Backlog page) */}
                <div className="al-legend">
                  <span style={{ fontWeight: 600 }}>Task dot = current status:</span>
                  {["In Progress", "Done", "Review", "Pause", "To Do"].map((st) => (
                    <span key={st} className="al-leg">
                      <span className="al-task-dot" style={{ background: STATUS_COLORS[st] }} /> {st}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .al-wrap { max-width: 900px; margin: 0 auto; display: flex; flex-direction: column; gap: 1rem;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; }
        .al-wrap .card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }

        .al-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 1rem; padding: 0.9rem 1.1rem; }
        .al-datenav { display: flex; align-items: center; gap: 0.25rem; }
        .al-toggle { display: flex; align-items: center; gap: 0.5rem; margin: 0 0 0 auto; font-size: 0.85rem; color: #475569; cursor: pointer; }

        /* Summary */
        .al-summary { padding: 1.1rem 1.25rem; }
        .al-summary-head { display: flex; justify-content: space-between; align-items: flex-start; }
        .al-devname { font-size: 1.15rem; font-weight: 700; color: #0f172a; }
        .al-date { font-size: 0.85rem; color: #64748b; margin-top: 2px; }
        .al-flagchip { display: inline-flex; align-items: center; gap: 5px; background: #fef2f2; color: #dc2626; border: 1px solid #fecaca;
          font-size: 0.75rem; font-weight: 700; padding: 4px 10px; border-radius: 999px; }
        .al-stats { display: grid; grid-template-columns: repeat(6, 1fr); gap: 0.75rem; margin-top: 1rem; }
        .al-stat { background: #f8fafc; border-radius: 8px; padding: 0.6rem 0.75rem; }
        .al-stat-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; }
        .al-stat-value { font-size: 1rem; font-weight: 600; color: #1e293b; margin-top: 2px; }

        /* Timeline */
        .al-timeline { padding: 1.25rem 1.25rem 1.25rem 0.75rem; }
        .al-row { display: flex; align-items: flex-start; }
        .al-time { width: 82px; min-width: 82px; text-align: right; font-size: 0.8rem; font-weight: 600; color: #334155;
          font-variant-numeric: tabular-nums; padding-top: 7px; }
        .al-spine { width: 46px; min-width: 46px; display: flex; flex-direction: column; align-items: center; align-self: stretch; }
        .al-dot { width: 30px; height: 30px; border-radius: 50%; border: 2px solid; display: flex; align-items: center; justify-content: center;
          font-size: 12px; z-index: 1; background: #fff; }
        .al-spine::after { content: ""; flex: 1; width: 2px; background: #e2e8f0; margin-top: -2px; }
        .al-row:last-child .al-spine::after { display: none; }

        .al-card { flex: 1; background: #fff; border: 1px solid #e2e8f0; border-left: 3px solid; border-radius: 8px;
          padding: 0.6rem 0.9rem; margin: 0 0 0.9rem 0.25rem; font-size: 0.9rem; color: #1e293b; line-height: 1.45; }
        .al-strong { font-weight: 600; }
        .al-danger { color: #dc2626; }
        .al-muted { color: #94a3b8; }
        .al-note { color: #64748b; font-size: 0.83rem; }
        .al-sub { font-size: 0.82rem; color: #64748b; margin-top: 4px; }
        .al-comment { background: #f8fafc; border-radius: 6px; padding: 4px 8px; }

        .al-task { display: inline-flex; align-items: center; gap: 5px; background: #f1f5f9; border-radius: 6px; padding: 1px 8px 1px 6px;
          font-weight: 600; font-size: 0.85rem; color: #0f172a; }
        .al-task-dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }

        /* Gap chips between events */
        .al-gaprow { min-height: 34px; }
        .al-gaprow .al-spine::after { display: none; }
        .al-gapline { flex: 1; width: 2px; background: #e2e8f0; }
        .al-gapline-paused { background: repeating-linear-gradient(180deg, #fca5a5, #fca5a5 4px, transparent 4px, transparent 8px); width: 3px; }
        .al-gapchip-wrap { display: flex; align-items: center; padding-bottom: 0.9rem; }
        .al-gapchip { display: inline-flex; align-items: center; gap: 5px; font-size: 0.75rem; color: #64748b; background: #f1f5f9;
          border-radius: 999px; padding: 3px 10px; font-style: italic; }
        .al-gapchip-paused { color: #b91c1c; background: #fef2f2; border: 1px solid #fecaca; font-style: normal; font-weight: 600; }
        .al-gapchip-working { color: #1d4ed8; background: #eff6ff; border: 1px solid #bfdbfe; font-style: normal; font-weight: 600; }

        .al-legend { display: flex; justify-content: center; flex-wrap: wrap; align-items: center; gap: 14px;
          font-size: 0.78rem; color: #475569; }
        .al-leg { display: inline-flex; align-items: center; gap: 5px; }

        .al-empty { display: flex; flex-direction: column; align-items: center; padding: 3rem; color: #94a3b8; }
        .al-empty .pi { font-size: 2rem; margin-bottom: 0.75rem; }

        @media (max-width: 640px) {
          .al-stats { grid-template-columns: repeat(3, 1fr); }
          .al-time { width: 64px; min-width: 64px; font-size: 0.72rem; }
        }
      `}</style>
    </div>
  );
};

export default ActivityLog;
